import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AI_CONFIG } from '../config/aiConfig';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

let autoMessageAlreadyShown = false;

const AIChat = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showAutoMessage, setShowAutoMessage] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const autoMessageTimer = useRef<NodeJS.Timeout | null>(null);

  // Auto-message display logic - appears after 8 seconds and disappears after 12 more seconds
  useEffect(() => {
    if (isOpen || autoMessageAlreadyShown) return;

    autoMessageTimer.current = setTimeout(() => {
      if (!isOpen && !autoMessageAlreadyShown) {
        setShowAutoMessage(true);

        autoMessageTimer.current = setTimeout(() => {
          setShowAutoMessage(false);
          autoMessageAlreadyShown = true; // Marque comme déjà affiché
        }, 5000);
      }
    }, 8000);

    return () => {
      if (autoMessageTimer.current) {
        clearTimeout(autoMessageTimer.current);
      }
    };
  }, [isOpen]);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage: Message = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const directApiKey = process.env.REACT_APP_DEEPSEEK_API_KEY;
      
      // If we have a direct API key, use it immediately
      if (directApiKey) {
        const response = await fetch('https://api.deepseek.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${directApiKey}`
          },
          body: JSON.stringify({
            model: AI_CONFIG.model,
            messages: [
              { role: 'system', content: AI_CONFIG.systemPrompt },
              ...messages,
              { role: 'user', content: input }
            ],
            temperature: AI_CONFIG.temperature,
            max_tokens: AI_CONFIG.maxTokens
          })
        });

        if (!response.ok) {
          throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log('API Response:', data);

        if (!data.choices || !data.choices[0] || !data.choices[0].message) {
          throw new Error('Invalid API response format');
        }

        const aiResponse: Message = {
          role: 'assistant',
          content: data.choices[0].message.content
        };

        setMessages(prev => [...prev, aiResponse]);
        return;
      }

      // Fallback: try serverless function if no direct API key
      if (!AI_CONFIG.apiEndpoint) {
        throw new Error('No AI backend configured');
      }

      const response = await fetch(AI_CONFIG.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: AI_CONFIG.model,
          messages: [
            { role: 'system', content: AI_CONFIG.systemPrompt },
            ...messages,
            { role: 'user', content: input }
          ],
          temperature: AI_CONFIG.temperature,
          max_tokens: AI_CONFIG.maxTokens
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('API Response:', data);

      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error('Invalid API response format');
      }

      const aiResponse: Message = {
        role: 'assistant',
        content: data.choices[0].message.content
      };

      setMessages(prev => [...prev, aiResponse]);
    } catch (error: any) {
      console.error('Error in handleSubmit:', error);
      const errorMessage: Message = {
        role: 'assistant',
        content: `Hello! The AI assistant isn't working right now. Please use the Upwork link to reach Mehdi: https://www.upwork.com/freelancers/~01902b7448e204d256?mp_source=share`
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle chat window and ensure auto message is hidden
  const toggleChat = () => {
    setIsOpen(prev => !prev);
    setShowAutoMessage(false); // Always hide auto message when toggling chat
  };

  return (
      <>
        {/* Floating button */}
        <motion.button
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleChat}
            className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-500 dark:to-blue-300 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
        >
          <motion.div
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
          >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
              <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
              />
            </svg>
          </motion.div>
        </motion.button>

        {/* Chat window */}
        <AnimatePresence>
          {isOpen && (
              <motion.div
                  key="chat-window"
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.8, y: 20 }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30
                  }}
                  className="fixed bottom-24 right-6 z-40 w-96 bg-white dark:bg-gray-100 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-300"
              >
                <div className="p-4 bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-500 dark:to-blue-300 flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
                    <h2 className="text-xl font-semibold text-white">AI Assistant</h2>
                  </div>
                  <button
                      onClick={toggleChat}
                      className="text-white hover:text-gray-200 transition-colors duration-200"
                  >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                      <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>

                <div className="h-96 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-blue-200 dark:scrollbar-thumb-gray-600">
                  {messages.map((message, index) => (
                      <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                            className={`max-w-[80%] p-3 rounded-2xl ${
                                message.role === 'user'
                                    ? 'bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-500 dark:to-blue-300 text-white'
                                    : 'bg-white dark:bg-gray-200 text-gray-800 dark:text-gray-800 shadow-sm'
                            }`}
                        >
                          {message.content}
                        </div>
                      </motion.div>
                  ))}
                  {isLoading && (
                      <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="flex justify-start"
                      >
                        <div className="bg-white dark:bg-gray-200 p-3 rounded-2xl shadow-sm">
                          <div className="flex space-x-2">
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" />
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce delay-100" />
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce delay-200" />
                          </div>
                        </div>
                      </motion.div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                  <div className="flex space-x-2">
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Type your message..."
                        className="flex-1 px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-200 text-gray-800 dark:text-gray-800 transition-all duration-200"
                    />
                    <motion.button
                        type="submit"
                        disabled={isLoading}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-500 dark:to-blue-300 text-white rounded-xl hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 transition-all duration-200"
                    >
                      <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                      >
                        <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z"
                            clipRule="evenodd"
                        />
                      </svg>
                    </motion.button>
                  </div>
                </form>
              </motion.div>
          )}
        </AnimatePresence>

        {/* Auto message bubble */}
        <AnimatePresence>
          {showAutoMessage && !isOpen && (
              <motion.div
                  key="auto-message"
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.8, y: 20 }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30
                  }}
                  className="fixed bottom-24 right-6 z-10 max-w-xs bg-white dark:bg-gray-100 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-300"
              >
                <div className="p-4 bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-500 dark:to-blue-300 flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
                  <h2 className="text-xl font-semibold text-white">AI Assistant</h2>
                </div>
                <div className="p-4">
                  <p className="text-gray-800 dark:text-gray-200">
                    Need help? Click the chat button!
                  </p>
                </div>
              </motion.div>
          )}
        </AnimatePresence>
      </>
  );
};

export default AIChat;